import { type CalculatorInput, type CalculatorInputValues } from '../../domain/CalculateRequest';
import { CALC_FIELD_PART_SEPERATOR, CASH_OPTION, FINANCE_OPTION, FINANCIAL_PRODUCT, FINANCIAL_PRODUCT_OPTION_DATA_TYPE } from '../constants';

export type FinanceOptions = Array<string>;
export type FinanceProduct = { name: string; financeOptions?: FinanceOptions };
export type FinanceProducts = Array<FinanceProduct>;

export const getDefaultFinanceProductFromData = (productData: FinanceProducts) => {
	return productData?.[0];
};

export const getDefaultFinanceOptionFromProductData = (productData: FinanceProduct) => {
	return productData.financeOptions ? productData.financeOptions[0] : undefined;
};

export const getProductFromFinanceData = (productData: FinanceProducts, productName: string) => {
	return productData?.find((product: FinanceProduct) => product.name === productName);
};

export const hasOptionsInFinanceProduct = (productData: FinanceProduct) => {
	return Object.hasOwn(productData, 'financeOptions');
};

export const getOptionFromProductData = (productData: FinanceProduct, optionName: string) => {
	return productData.financeOptions?.find((option: string) => option === optionName);
};

export const getFinanceOption = (productData: FinanceProduct, optionName: string) => {
	if (hasOptionsInFinanceProduct(productData)) {
		const matchingFinanceOption = getOptionFromProductData(productData, optionName);

		if (matchingFinanceOption) {
			return matchingFinanceOption;
		} else {
			return getDefaultFinanceOptionFromProductData(productData);
		}
	} else {
		return undefined;
	}
};

export const getFinanceProduct = (productData: FinanceProducts, productName: string) => {
	const matchedFinanceProduct = getProductFromFinanceData(productData, productName);

	if (matchedFinanceProduct) {
		return matchedFinanceProduct;
	} else {
		return getDefaultFinanceProductFromData(productData);
	}
};

export const isCashSelected = (inputValues: CalculatorInputValues): boolean => {
	return inputValues.some((inputValue) => inputValue.field === FINANCIAL_PRODUCT && inputValue.value === CASH_OPTION);
};

export const getFinanceProductFromInputValues = (inputValues: CalculatorInputValues) => {
	const financialProductField = inputValues.find((input) => input.field === FINANCIAL_PRODUCT);

	if (financialProductField) {
		return financialProductField.value as string;
	}

	return CASH_OPTION;
};

export const getFinanceOptionFromInputValues = (inputValues: CalculatorInputValues) => {
	const financialOptionField = inputValues.find((input) => input.field === FINANCE_OPTION);

	if (financialOptionField) {
		return financialOptionField.value as string;
	}

	return undefined;
};

export function getCombinedFinanceName(inputValues: CalculatorInputValues): string {
	const financeProduct = getFinanceProductFromInputValues(inputValues);
	const financeOption = getFinanceOptionFromInputValues(inputValues);

	if (financeOption && financeOption.length) {
		return financeProduct + '_' + financeOption;
	}

	return financeProduct;
}

export function encodeInputValue(input: CalculatorInput) {
	return tfsInputValuesToUrlParam(input.field, input.value.toString(), input.dataType);
}

export function tfsInputValuesToUrlParam(field: string, value: string, dataType: string) {
	return encodeURIComponent(`${field}${CALC_FIELD_PART_SEPERATOR}${value}${CALC_FIELD_PART_SEPERATOR}${dataType}`);
}

export function decodeInputValue(inputUrlParam: string) {
	const decodedField = decodeURIComponent(inputUrlParam);
	const [field, value, dataType] = decodedField.split(CALC_FIELD_PART_SEPERATOR);
	if (!field || !value || !dataType) {
		return undefined;
	}
	return { field, value, dataType };
}

export function getFinanceProductInputValue(productName: string) {
	return { dataType: FINANCIAL_PRODUCT_OPTION_DATA_TYPE, field: FINANCIAL_PRODUCT, value: productName };
}

export function getFinanceOptionInputValue(optionName: string) {
	return { dataType: FINANCIAL_PRODUCT_OPTION_DATA_TYPE, field: FINANCE_OPTION, value: optionName };
}

export function isInputValueDefined(item: CalculatorInput | undefined): item is CalculatorInput {
	return !!item;
}
/**
 * Checks whether finance product and options in calculator inputs are valid by comparing them to,
 * the list of finance products and their options returned from TFS.  It returns an array, the array will
 * be empty if the values are valid.  If not it contains the values we need to set to make a valid finance
 * product and option.
 * @param inputFields - The current finance calculator input fields
 * @param financeProducts - The finance data from TFS which shows us valid finance products and their options
 * @param defaultProduct -The default product from the TFS endpoint
 * @param defaultOption -The default option from the TFS endpoint
 * @returns an array of calculator inputs we need to override or add to make a valid configuration
 */
export function validateFinancialProduct(
	inputFields: CalculatorInputValues,
	financeProducts: FinanceProducts,
	defaultProduct: string,
	defaultOption?: string
) {
	const financeProductInSearchParams = inputFields.find((field) => field.field === FINANCIAL_PRODUCT);
	const financeOptionInSearchParams = inputFields.find((field) => field.field === FINANCE_OPTION);

	const sanitisedFinanceProductName = getFinanceProduct(
		financeProducts,
		(financeProductInSearchParams?.value as string) || defaultProduct || ''
	);
	const sanitisedFinanceOptionName = sanitisedFinanceProductName
		? getFinanceOption(sanitisedFinanceProductName, (financeOptionInSearchParams?.value as string) || defaultOption || '')
		: undefined;

	const overideValues: CalculatorInputValues = [];

	if (
		sanitisedFinanceProductName?.name === financeProductInSearchParams?.value &&
		sanitisedFinanceOptionName === financeOptionInSearchParams?.value
	) {
		// Options are valid we dont need to do anything so return empty array
		return overideValues;
	}

	if (sanitisedFinanceProductName) {
		overideValues.push(getFinanceProductInputValue(sanitisedFinanceProductName.name));
	}

	if (sanitisedFinanceOptionName) {
		overideValues.push(getFinanceOptionInputValue(sanitisedFinanceOptionName));
	}
	return overideValues;
}
